import {
  useCallback, useState, useEffect, useRef,
} from 'react'
import clsx from 'clsx'
import { useHistory } from 'react-router-dom'
import { useForm, FormProvider, Form } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormattedMessage, useIntl } from 'react-intl'
import * as Yup from 'yup'

import { useLoader } from '@/providers/loader/LoaderProvider'
import { useSession } from '@/providers/session/SessionProvider'
import useAuthenticate from '@/hooks/useAuthenticate'
import InputField from '@/components/Form/InputField'
import PasswordField from '@/components/Form/PasswordField'

import { CREATE_ACCOUNT_URL, EMAIL_REGEX } from '@/constants'
import { urls, passwordRecoverCount } from 'Config'

import {
  NEW_PASSWORD,
  LOCKED_MEMBER,
  RECOVER_PASSWORD,
  RESET_PASSWORD,
} from '@/routes'
import './Login.scss'

// The password is optional in the schema: it is only asked for after the email step, and SSO users
// never get that far because they sign in through their identity provider.
const schema = Yup.object().shape({
  username: Yup.string().matches(EMAIL_REGEX).required(),
  password: Yup.string(),
})

const Login = () => {
  const { setLoading } = useLoader()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { checkProvider, setUserEmail, uiTab } = useSession()
  const authenticate = useAuthenticate()
  const submitCount = useRef(0)
  const [error, setError] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const loginForm = useForm({
    defaultValues: { username: '', password: '' },
    resolver: yupResolver(schema),
  })

  const { setValue } = loginForm
  const { isDirty, isSubmitting } = loginForm.formState
  const [username, password] = loginForm.watch(['username', 'password'])
  const canSubmit = isDirty && !isSubmitting && (!showPassword || Boolean(password))

  useEffect(() => history.listen((location, action) => {
    if (action === 'POP' && (location.pathname === NEW_PASSWORD)) {
      history.push('/')
    }
  }, [history]))

  // Back to the email step (the "Change" link): the SSO check runs again for whatever e-mail is
  // submitted next, and the failed-attempt count starts over.
  const handleChangeEmail = useCallback(() => {
    setShowPassword(false)
    setValue('password', '')
    submitCount.current = 0
  }, [setValue])

  // A credentials error describes the previous attempt, so it goes away as soon as the user edits.
  useEffect(() => setError(false), [username, password])

  const handleSubmit = useCallback(
    async ({ data }) => {
      // `data` is the form's live values object, so it changes with the field: keep what was submitted.
      const submittedEmail = data.username
      const emailEditedSince = () => loginForm.getValues('username') !== submittedEmail
      setError(false)
      try {
        setLoading(true)
        if (!showPassword) {
          const ssoDomain = await checkProvider(data)
          // The e-mail was edited while the check was pending: that one was never checked.
          if (emailEditedSince()) return
          if (ssoDomain) {
            window.location.assign(`${urls.ssoApi}${ssoDomain}/${uiTab
              ? `?${encodeURIComponent(`returnurl=/dashboard.aspx#${uiTab}`)}`
              : ''
            }`)
            return
          }
          setUserEmail(submittedEmail)
          setShowPassword(true)
          return
        }
        await authenticate(data)
        submitCount.current = 0
      } catch (err) {
        if (!showPassword && emailEditedSince()) return
        if (showPassword && err.response?.data?.is_member_locked) {
          history.push(LOCKED_MEMBER)
          return
        }
        setError(true)
        if (showPassword) {
          submitCount.current += 1
          if (submitCount.current >= passwordRecoverCount) history.push(RECOVER_PASSWORD)
        }
      } finally {
        setLoading(false)
      }
    },
    [authenticate, checkProvider, history, loginForm, setLoading, setUserEmail, showPassword, uiTab],
  )

  // The reset flow needs to know whose password to reset. The link only shows once the email passed
  // the first step, and editing the email hides it again.
  const handleForgotPassword = useCallback(() => {
    setUserEmail(username)
    history.push({ pathname: RESET_PASSWORD })
  }, [history, setUserEmail, username])

  return (
    <div className="content-wrapper main-login">
      <FormProvider {...loginForm}>
        <div className={clsx('login-titles', { 'login-titles--compact': showPassword })}>
          {showPassword ? (
            <h1 className="title"><FormattedMessage id="loginMain.enterPassword" /></h1>
          ) : (
            <>
              <h1 className="title"><FormattedMessage id="loginMain.welcomeBack" /></h1>
              <p className="sub-title"><FormattedMessage id="loginMain.signInSubtitle" /></p>
            </>
          )}
        </div>
        <Form onSubmit={handleSubmit} className="login-form-wrapper">
          <ul className="login-field-wrapper">
            {error && (
              <li>
                <ul className="error-section">
                  <li className="incorrect">
                    <span role="alert" data-testid="login-error">
                      {<FormattedMessage id="loginMain.checkID" />}
                    </span>
                  </li>
                </ul>
              </li>
            )}
            {showPassword ? (
              <>
                <li className="email-chip-item login-fade-in">
                  <div className="email-chip" data-testid="login-email-chip">
                    <span className="email-chip__icon" aria-hidden="true" />
                    <span className="email-chip__address" data-testid="login-email-value">{username}</span>
                    <button
                      type="button"
                      data-testid="login-change-email"
                      className="email-chip__change"
                      onClick={handleChangeEmail}
                    >
                      <FormattedMessage id="buttons.change" />
                    </button>
                  </div>
                </li>
                <li className="login-fade-in">
                  <PasswordField
                    name="password"
                    labelProps={{ label: formatMessage({ id: 'loginMain.password' }) }}
                    labelAction={(
                      <button
                        type="button"
                        data-testid="login-forgot-password"
                        onClick={handleForgotPassword}
                        className="login-blue-link login-link-button"
                      >
                        {<FormattedMessage id="loginMain.forgotPasswordLink" />}
                      </button>
                    )}
                    inputProps={{
                      'data-testid': 'login-password',
                      placeholder: formatMessage({ id: 'placeholders.password' }),
                      autoFocus: true,
                    }}
                    toggleLabel={formatMessage({ id: 'loginMain.togglePassword' })}
                  />
                </li>
              </>
            ) : (
              <li>
                <InputField
                  name="username"
                  labelProps={{ label: formatMessage({ id: 'loginMain.email' }) }}
                  inputProps={{
                    autoComplete: 'username',
                    autoFocus: true,
                    'data-testid': 'login-email',
                    placeholder: formatMessage({ id: 'placeholders.emailOrId' }),
                    maxLength: 100,
                  }}
                  errorProps={{ message: formatMessage({ id: 'loginMain.invalidUsername' }) }}
                />
              </li>
            )}
            <li className={clsx('actions', { 'actions--password': showPassword })}>
              <button
                data-testid="login-submit"
                disabled={!canSubmit}
                type="submit"
                className={clsx('login-button', { blue: canSubmit })}
              >
                {<FormattedMessage id={showPassword ? 'buttons.signIn' : 'buttons.next'} />}
              </button>
            </li>
          </ul>
        </Form>
        {!showPassword && (
          <div className="no-account">
            <FormattedMessage id="loginMain.noAccount" />
            {' '}
            <a
              data-testid="login-sign-up"
              href={CREATE_ACCOUNT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage id="loginMain.signUp" />
            </a>
          </div>
        )}
      </FormProvider>
    </div>
  )
}

export default Login
